/*global MAINTENANCE_MODE */

import React from 'react';
import Cookies from 'universal-cookie';
import { Route, IndexRedirect, IndexRoute } from 'react-router';

import { Paths } from 'utils/paths'
import { canUseDevicesSelector, canSeeDesignGuide } from 'selectors/users'

// Top level component
import App from 'components/Application';
import TopLevelComponent from 'components/TopLevel';

// Routes
import Landing from 'views/Landing';
import Maintenance from 'views/Maintenance';
import NotFound from 'views/NotFound';
import UsernameError from 'views/UsernameError';
import SignOut from 'views/Authentication/sign_out';

export default (store) => {
  // this is an onEnter wrapper, it returns a proper onEnter function
  const catchNoUsername = (nextOnEnterFunc) => {
    return (nextState, replace) => {
      const state = store.getState()
      if (state.session && state.session.user &&
          nextState.location.pathname !== Paths.noUsername() &&
          (!state.session.user.username || state.session.user.username.length === 0)) {
        // if there is a user but the user has no username, direct them immediately to noUsername
        replace(Paths.noUsername())
      } else {
        // otherwise, handoff to nextOnEnterFunc
        nextOnEnterFunc(nextState, replace)
      }
    }
  }

  const catchMaintenanceMode = (nextOnEnterFunc) => {
    return (nextState, replace) => {
      if (MAINTENANCE_MODE && !/maintenance/.test(nextState.location.pathname)) {
        replace('/maintenance')
      } else {
        nextOnEnterFunc(nextState, replace)
      }
    }
  }

  const deviceAccessRequired = nextOnEnterFunc => (nextState, replace) => {
    canUseDevicesSelector(store.getState())
      ? nextOnEnterFunc(nextState, replace)
      : replace(Paths.root())
  }

  const designGuideAccessRequired = (nextState, replace) => {
    canSeeDesignGuide(store.getState()) || replace(Paths.root())
  }

  // an onEnter handler for react-router that can redirect unauthenticated sessions
  // https://github.com/reactjs/react-router/blob/master/docs/API.md#onenternextstate-replace-callback
  const authenticationRequired = (nextState, replace) => {
    const state = store.getState()
    if (!state.session.user) {
      console.error("failed to load user from session", state.session)
      replace(Paths.root())
    }
  }

  // redirect unauthenticated sessions and sessions not owning the path to the
  // public page belonging to the :username in the path
  const ownershipRequired = (nextState, replace) => {
    const state = store.getState()

    // NOTE: this isn't necessarily insecure, since all the API calls
    // required to load data will still be authenticated even if someone hacks
    // the data load to force a user role
    const isAnon = !state.session.user
    const isNotUser = isAnon || (nextState.params.username !== state.session.user.username)
    const hasAuthorityToAttempt = !isAnon && (state.session.user.role === 'admin' || state.session.user.role === 'developer')

    if ((isAnon || isNotUser) && !hasAuthorityToAttempt) {
      replace(Paths.public_user({ username: nextState.params.username }))
    }
  }

  // if io-rails set a redirect-after-sign-in cookie, navigate to it right away
  const redirectFromCookie = (nextState, replace) => {
    const cookies = new Cookies();
    const redir = cookies.get('redirect_to')
    if (! redir) { return }
    cookies.remove('redirect_to')

    const pathname = decodeURIComponent(redir)
    if (Paths.isSafe(pathname)) {
      replace(pathname)
    }
  }

  // redirect to home path when authenticated. Add this to Routes or
  // IndexRoutes that are only intended for unauthenticated visitors.
  const redirectAfterAuthentication = (nextState, replace) => {
    const { session } = store.getState()
    if (session?.user) {
      replace(Paths.home({ username: session.user.username }))
    }
  }

  // redirect to the :username version of the given path when authenticated.
  const redirectToUserAfterAuthentication = path_selector => {
    return (nextState, replace) => {
      const { session } = store.getState()

      session?.user
        ? replace(Paths[path_selector]({ username: session.user.username, ...nextState.params }))
        // if no user is logged in, redirect to landing
        : replace(Paths.root())
    }
  }

  // when legacy users don't have a username in the system yet, we redirect
  // them to accounts.adafruit. Once they've created a username they'll be
  // redirected here
  const redirectToRootIfUsernameExistsNow = (nextState, replace) => {
    const { session } = store.getState()

    if (session?.user?.username?.length > 0) {
      replace(Paths.home({ username: session.user.username }))
    }
  }

  const catchLegacyDashboardPath = ({ params }, replace) => {
    /^[^/]+$/.test(params.splat)
      // params.splat is a single path component, try redirecting to /:username/dashboards/:splat
      ? replace(Paths.dashboard({ username: params.username, key: params.splat }))
      : replace(Paths.notfound())
  }

  const catchLegacyTriggerPath = ({ params }, replace) => {
    params.id
      ? replace(Paths.action({ username: params.username, id: params.id }))
      : replace(Paths.actions({ username: params.username }))
  }

  const TopLevelView = (loader, cb) => {
    return loader.then(view => {
      cb(null, {main: TopLevelComponent(view.default)})
    })
  }

  return (
    <Route path="/" onEnter={catchMaintenanceMode(catchNoUsername(redirectFromCookie))}>
      <IndexRoute component={Landing} onEnter={redirectAfterAuthentication} />
      <Route path="/welcome" component={Landing} />

      {/* shortcut routes */}
      <Route path="/profile" onEnter={redirectToUserAfterAuthentication('profile')} />
      <Route path="/activities" onEnter={redirectToUserAfterAuthentication('activities')} />
      <Route path="/dashboards" onEnter={redirectToUserAfterAuthentication('dashboards')} />
      <Route path="/feeds" onEnter={redirectToUserAfterAuthentication('feeds')} />
      <Route path="/wippersnapper" onEnter={redirectToUserAfterAuthentication('wippers')} />
      <Route path="/wippersnapper/new" onEnter={redirectToUserAfterAuthentication('new_wipper')} />
      <Route path="/wippersnapper/new/:boardName" onEnter={redirectToUserAfterAuthentication('update_wipper')} />
      <Route path="/triggers" onEnter={redirectToUserAfterAuthentication('triggers')} />
      <Route path="/actions" onEnter={redirectToUserAfterAuthentication('triggers')} />
      <Route path="/monitor" onEnter={redirectToUserAfterAuthentication('monitor')} />
      <Route path="/sharing" onEnter={redirectToUserAfterAuthentication('sharing')} />
      <Route path="/billing" onEnter={redirectToUserAfterAuthentication('billing')} />

      <Route path="/services" onEnter={redirectToUserAfterAuthentication('services')} />
      <Route path="/services/time" onEnter={redirectToUserAfterAuthentication('service_time')} />
      <Route path="/services/ifttt" onEnter={redirectToUserAfterAuthentication('service_ifttt')} />
      <Route path="/services/weather" onEnter={redirectToUserAfterAuthentication('service_weather')} />
      <Route path="/services/words" onEnter={redirectToUserAfterAuthentication('service_words')} />
      <Route path="/services/zapier" onEnter={redirectToUserAfterAuthentication('service_zapier')} />

      <Route component={App}>
        {/* non-username pages */}
        <Route path="/contact"  getComponents={(_, cb) => TopLevelView(import('views/Landing/support'), cb)} />
        <Route path="/support"  getComponents={(_, cb) => TopLevelView(import('views/Landing/support'), cb)} />
        <Route path="/feedback" getComponents={(_, cb) => TopLevelView(import('views/Landing/support'), cb)} />

        <Route path="/faq"         getComponents={(_, cb) => TopLevelView(import('views/Landing/faq'), cb)} />
        <Route path="/plus"        getComponents={(_, cb) => TopLevelView(import('views/Landing/plus'), cb)} />
        <Route path="/icons-faq"   getComponents={(_, cb) => TopLevelView(import('views/Landing/icons-faq'), cb)} />
        <Route path="/help"        getComponents={(_, cb) => TopLevelView(import('views/Landing/faq'), cb)} />
        <Route path="/promotions"  getComponents={(_, cb) => TopLevelView(import('views/Landing/promotions'), cb)} />
        <Route path="/terms"       getComponents={(_, cb) => TopLevelView(import('views/Landing/terms'), cb)} />
        <Route path="/unsubscribe" getComponents={(_, cb) => TopLevelView(import('views/Unsubscribe/index'), cb)} />

        <Route path="/maintenance" components={{main: Maintenance}} />
        <Route path="/sign_out"    components={{main: TopLevelComponent(SignOut)}} onEnter={authenticationRequired} />

        <Route path="/upgrade"         onEnter={authenticationRequired} getComponents={(_, cb) => TopLevelView(import('views/Subscriptions/payment_form_container'), cb)} />
        <Route path="/upgrade/cancel"  onEnter={authenticationRequired} getComponents={(_, cb) => TopLevelView(import('views/Subscriptions/cancellation_form'), cb)} />
        <Route path="/upgrade/payment" onEnter={authenticationRequired} getComponents={(_, cb) => TopLevelView(import('views/Subscriptions/payment_change_container'), cb)} />
        <Route path="/upgrade/receipt" onEnter={authenticationRequired} getComponents={(_, cb) => TopLevelView(import('views/Subscriptions/payment_receipt_container'), cb)} />

        {/* error pages */}
        <Route path="/notfound" components={{main: NotFound}}/>
        <Route path="/username-error" components={{main: UsernameError}} onEnter={redirectToRootIfUsernameExistsNow} />

        <Route path=":username">
          <IndexRedirect to="wippersnapper" />

          <Route path="block/:username" onEnter={ownershipRequired} getComponents={(_, cb) => TopLevelView(import('views/SocialBlock/index'), cb)} />
          <Route path="share/:token" onEnter={ownershipRequired}    getComponents={(_, cb) => TopLevelView(import('views/Profile/share_review_form_container'), cb)} />

          <Route path="profile" onEnter={ownershipRequired} getComponents={(_, cb) => TopLevelView(import('views/Profile/show'), cb)}/>

          <Route path="billing">
            <IndexRoute onEnter={ownershipRequired} getComponents={(_, cb) => TopLevelView(import('views/Profile/billing'), cb)} />
            <Route path="invoice/:key" onEnter={ownershipRequired} getComponents={(_, cb) => TopLevelView(import('views/Profile/invoice'), cb)} />
          </Route>

          <Route path="monitor" onEnter={ownershipRequired} getComponents={(_, cb) => TopLevelView(import('views/Profile/monitor'), cb)} />
          <Route path="sharing" onEnter={ownershipRequired} getComponents={(_, cb) => TopLevelView(import('views/Profile/sharing'), cb)}/>

          <Route path="services" onEnter={ownershipRequired} getComponents={(_, cb) => TopLevelView(import('views/Services/index'), cb)}/>
          <Route>
            <Route path="services/ifttt" onEnter={ownershipRequired} getComponents={(_, cb) => TopLevelView(import('views/Services/ifttt'), cb)}/>
            <Route path="services/zapier" onEnter={ownershipRequired} getComponents={(_, cb) => TopLevelView(import('views/Services/zapier'), cb)}/>
            <Route path="services/weather" onEnter={ownershipRequired} getComponents={(_, cb) => TopLevelView(import('views/Services/weather'), cb)} />
            <Route path="services/words" onEnter={ownershipRequired} getComponents={(_, cb) => TopLevelView(import('views/Services/words'), cb)}/>
            <Route path="services/time" onEnter={ownershipRequired} getComponents={(_, cb) => TopLevelView(import('views/Services/time'), cb)}/>
          </Route>

          <Route path="public" getComponents={(_, cb) => TopLevelView(import('views/Profile/public'), cb)} />

          <Route>
            <Route path="activities" onEnter={ownershipRequired} getComponents={(_, cb) => TopLevelView(import('views/Profile/activities'), cb)}/>

            <Route path="dashboards/:id" getComponents={(_, cb) => TopLevelView(import('views/Dashboards/dashboard_container'), cb)} />
            <Route path="feeds/:key" getComponents={(_, cb) =>  TopLevelView(import('views/Feeds/show'), cb)} />

            {/* these are children of protected paths that don't require auth */}
            <Route path="dashboards" onEnter={ownershipRequired} getComponents={(_, cb) => TopLevelView(import('views/Dashboards/list'), cb)} />

            <Route path="feeds" onEnter={ownershipRequired} getComponents={(_, cb) =>  TopLevelView(import('views/Feeds/list'), cb)} />

            <Route path="groups/:key" getComponents={(_, cb) => TopLevelView(import('views/Groups/show'), cb)} />
            <Route path="groups/:group_key/feeds/:key" getComponents={(_, cb) => TopLevelView(import('views/Feeds/show'), cb)} />

            <Route path="wippersnapper" onEnter={ownershipRequired} getComponents={(_, cb) => TopLevelView(import('views/WipperSnapper/list'), cb)} />
            <Route path="wippersnapper/new" onEnter={ownershipRequired} getComponents={(_, cb) => TopLevelView(import('views/WipperSnapper/new/index'), cb)} />
            <Route path="wippersnapper/new/:boardName" onEnter={ownershipRequired} getComponents={(_, cb) => TopLevelView(import('views/WipperSnapper/new/index'), cb)} />
            <Route path="wippersnapper/:key" onEnter={ownershipRequired} getComponents={(_, cb) => TopLevelView(import('views/WipperSnapper/show'), cb)} />

            <Route path="triggers" onEnter={catchLegacyTriggerPath} />
            <Route path="triggers/:id" onEnter={catchLegacyTriggerPath} />
            <Route path="actions" onEnter={ownershipRequired} getComponents={(_, cb) =>  TopLevelView(import('views/Triggers/list'), cb)} />
            <Route path="actions/new" onEnter={ownershipRequired} getComponents={(_, cb) =>  TopLevelView(import('views/Triggers/form'), cb)} />
            <Route path="actions/:id/edit" onEnter={ownershipRequired} getComponents={(_, cb) =>  TopLevelView(import('views/Triggers/form'), cb)} />
            <Route path="devices" onEnter={deviceAccessRequired(ownershipRequired)} getComponents={(_, cb) => TopLevelView(import('views/Devices/list'), cb)} />
            <Route path="devices/:id" onEnter={deviceAccessRequired(ownershipRequired)} getComponents={(_, cb) => TopLevelView(import('views/Devices/show'), cb)} />

            <Route path="design_guide" onEnter={designGuideAccessRequired} getComponents={(_, cb) => TopLevelView(import('views/DesignGuide/list'), cb)} />
          </Route>

          {/* fall through gets legacy Dashboard redirect or Not Found */}
          <Route path="*" onEnter={catchLegacyDashboardPath} />
        </Route>
      </Route>
    </Route>
  );
}
